import {useMemo} from 'react';
import {ColumnDef} from '@tanstack/react-table';
import TableMetronic from '../../../app/common/components/table/TableMetronic';
import {ListService} from '../Types/service';
import IconeService from './IconeService';
import ECV1Link from '../../../app/common/components/ECV1Link';
import {useIntl} from 'react-intl';
import {useGetMe} from '../../../app/hooks/useGetMe';
import {isClientCIS} from "../../Auth/Utils/CustomerHelper";
import {
  accessorService,
  accessorSite,
  getBuColLabel,
  getServiceColLabel,
  getSiteColLabel
} from "../Helpers/ServiceHelper";
import {createSearchParams, useNavigate} from 'react-router-dom';

interface HebergementProps {
  isLoading: boolean;
  services?: ListService[];
}

const Hebergement = ({isLoading, services}: HebergementProps): JSX.Element => {
    const customer = useGetMe();
    const intl = useIntl();
    const navigate = useNavigate();

    const columns = useMemo<ColumnDef<ListService>[]>(() => {
          return [
            {
              header: 'BU',
              accessorKey: 'buId',
              cell: getBuColLabel
            },
            {
              header: 'Site',
              accessorKey: 'siteId',
              cell: getSiteColLabel,
              accessorFn: accessorSite,
              sortingFn: (rowA, rowB) => {
                const idA = rowA.original.site?.id ?? 0;
                const idB = rowB.original.site?.id ?? 0;
                return idA - idB;
              }
            },
            {
              header: 'Service',
              accessorKey: 'serviceId',
              cell: getServiceColLabel,
              accessorFn: accessorService,
              sortingFn: (rowA, rowB) => {
                const idA = rowA.original.serviceId ?? 0;
                const idB = rowB.original.serviceId ?? 0;
                return idA - idB;
              }
            },
            {
              header: 'Accédez à vos services',
              accessorKey: 'domaine',

              cell: (info) => (
                <>
                  {isClientCIS(customer) ?
                    <>
                      <IconeService
                        ariaLabel={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.DATABASE',
                          })}
                        onClick={() => {
                          const row = info.row.original
                          const searchParams = new URLSearchParams();
                          searchParams.append('buId', row.buId.toString() );
                          searchParams.append('CISID', row.serviceId.toString() );
                          navigate({
                            pathname: `/services/database`,
                            search: createSearchParams(searchParams).toString(),
                          });
                        }}
                        iconClass="fs-1 fa-solid fa-database"
                        title={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.DATABASE',
                          })}
                        targetLink="_blank"
                      />
                      { info.row.original.is_domain && <IconeService
                        ariaLabel={intl.formatMessage({
                          id: 'DOCUMENTS.HEBERGEMENT.DOMAIN',
                        })}
                        onClick={() => {
                          const row = info.row.original
                          const searchParams = new URLSearchParams();
                          searchParams.append('domaine', row.link_name.toString() );
                          navigate({
                            pathname: `/services/domaine/infos`,
                            search: createSearchParams(searchParams).toString(),
                          });
                        }}
                        iconClass="fs-1 fa-solid fa-globe"
                        title={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.DOMAIN'})}
                        targetLink="_blank"
                      /> }
                      
                      {/* Site web / FTP */}
                      <ECV1Link path={'services/etat-web-ftp'}>
                        <IconeService
                          ariaLabel={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.SITE.WEB',
                          })}
                          iconClass="fs-1 fa-solid fa-earth-americas"
                          title={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.SITE.WEB',
                          })}
                        />
                      </ECV1Link>
                      
                      {/* Baies serveurs */}
                      <ECV1Link path={'hebergement/consommationelectrique'}>
                        <IconeService
                          ariaLabel={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.SERVERS',
                          })}
                          iconClass="fs-1 fa-solid fa-server"
                          title={intl.formatMessage({
                            id: 'DOCUMENTS.HEBERGEMENT.SERVERS',
                          })}
                        />
                      </ECV1Link>
                      
                      {/* Compte web / FTP et Consommation */}
                      <IconeService
                        ariaLabel={intl.formatMessage({
                          id: 'DOCUMENTS.HEBERGEMENT.SITE.WEB',
                        })}
                        onClick={() => {
                          const row = info.row.original
                          const searchParams = new URLSearchParams();
                          searchParams.append('buId', row.buId.toString() );
                          navigate({
                            pathname: `/services/ftp`,
                            search: createSearchParams(searchParams).toString(),
                          });
                        }}
                        iconClass="fs-1 fa-solid fa-key"
                        title="compte ftp / web et consommation"
                        targetLink="_blank"
                      />

                      {/* Comptes messageries */}
                      { info.row.original.is_domain && 
                        <IconeService 
                          ariaLabel={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.MESSAGERIE',})}
                          onClick={() => {
                            const row = info.row.original;
                            
                            const searchParams = new URLSearchParams();
                            searchParams.append('buId', row.buId.toString() );
                            searchParams.append('link_name', row.link_name.toString() );
                            searchParams.append('serviceId', row.serviceId.toString() );
                            navigate({
                              pathname: `/services/domain/emails`,
                              search: createSearchParams(searchParams).toString(),
                            });
                          }}
                          iconClass="fs-1 fa-solid fa-envelope"
                          title="Comptes messageries"
                          targetLink="_blank"
                        />
                      }
                    </> :
                    <>
                      {/* Nom de domaine */}
                      <IconeService
                        lien="https://extranet2.stella-telecom.fr//domaine"
                        ariaLabel={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.DOMAIN'})}
                        iconClass="fs-1 fa-solid fa-globe"
                        title={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.DOMAIN'})}
                        targetLink="_blank"
                      />
                      
                      {/* Site web / FTP */}
                      <IconeService
                        lien="https://extranet2.stella-telecom.fr/web"
                        ariaLabel={intl.formatMessage({
                          id: 'DOCUMENTS.HEBERGEMENT.SITE.WEB',
                        })}
                        iconClass="fs-1 fa-solid fa-earth-americas"
                        title={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.SITE.WEB'})}
                        targetLink="_blank"
                      />
                      
                      {/* Messagerie */}
                      <IconeService
                        lien="https://integralb.stella-telecom.fr/subscribe"
                        ariaLabel={intl.formatMessage({
                          id: 'DOCUMENTS.HEBERGEMENT.MESSAGERIE',
                        })}
                        iconClass="fs-1 fa-solid fa-envelope"
                        title={intl.formatMessage({id: 'DOCUMENTS.HEBERGEMENT.MESSAGERIE'})}
                        targetLink="_blank"
                      />

                    </>
                  }
                </>
              ),
            },
          ];
        }, [customer, intl , navigate ]
      )
    ;

    return (
      <>
        <TableMetronic<ListService>
          title="Accédez à vos services d'hébergement"
          data={services ?? []}
          columns={columns}
          isLoading={isLoading}
          defaultSorting={[
            {
              id: 'siteId',
              desc: true,
            },
          ]}
          searchBar
          pagination
        />
      </>
    );
  }
;

export default Hebergement;
